// Panel geometry, in px. The panel always sits in a dock: a bottom corner of the content area (right, the default,
// or left), or the side dock, the window's full height at its right edge, where the app narrows beside it.
// Minimums yield to the available space on small screens.
const WIDTH_RATIO = 1 / 3;
const MIN_WIDTH = 404;
/** Width of the thread-history column attached to the panel's conversation. */
export const HISTORY_WIDTH = 260;
/** Below this the conversation stops being readable, so the history column is not offered at all. */
const MIN_CONVERSATION_WIDTH = 320;
const MIN_HEIGHT = 320;
const SIDE_GAP = 24;
const TOP_GAP = 24;
/** Above the row along the window's bottom edge, bottom right, when one is reserved. */
export const BOTTOM_GAP = 96;
/** The side dock's widest share of the window, so the page keeps the rest. */
const SIDE_DOCK_MAX_SHARE = 0.4;
/** Under this window width the side dock is not offered: 404px is 40% of 1010. */
const SIDE_DOCK_MIN_VIEWPORT = 1024;
/** A pointer this close to the window's right edge drops the panel into the side dock. */
const SIDE_DOCK_EDGE = 48;

export type PanelDock = 'left' | 'right' | 'side';
export type PanelCorner = Exclude<PanelDock, 'side'>;

/**
 * Where the panel sits and at what size, as remembered. `corner` is the corner the side dock returns to. The width
 * is the conversation's, the history column comes on top of it; a null width or height is the dock's own default.
 */
export interface PanelPlacement {
    side: PanelDock;
    corner: PanelCorner;
    width: number | null;
    height: number | null;
}

export const DEFAULT_PLACEMENT: PanelPlacement = { side: 'right', corner: 'right', width: null, height: null };

interface Rect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface Viewport {
    width: number;
    height: number;
}

/** A size the user can change, with the bounds its dock allows. */
export interface PanelExtent {
    now: number;
    min: number;
    max: number;
}

export interface PanelFrameInput {
    placement: PanelPlacement;
    isExpanded: boolean;
    isHistoryOpen: boolean;
    /** Whether a row sits along the window's bottom edge, for the panel docked bottom right to stop above. */
    reserveBottomRow: boolean;
    contentRect: Rect | null;
    viewport: Viewport;
}

export interface PanelFrame {
    /** The dock the panel shows in: the remembered one, or its corner where the side dock is not offered. */
    dock: PanelDock;
    box: Rect;
    /** The box as fixed-position style, held by the dock's outer edge, so a width change grows it inwards. */
    style: { left?: number; right?: number; top: number; width: number; height: number };
    /** The conversation's width; null while expanded. */
    width: PanelExtent | null;
    /** The panel's height in a corner; null in the side dock, which is always the window's height, and expanded. */
    height: PanelExtent | null;
    /** False on a content area too narrow for the panel and its gaps: it stays bottom right and does not move. */
    canMove: boolean;
    canSideDock: boolean;
}

export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const extent = (value: number, min: number, max: number): PanelExtent => ({
    now: clamp(Math.round(value), min, max),
    min,
    max,
});

/**
 * The content area as wide as it is with no side dock, from its left edge to the window's right edge: the side dock
 * narrows the content area itself, and a panel sized off that would shrink the dock that narrows it. Before the
 * content area is measured, the whole window.
 */
const dockArea = (contentRect: Rect | null, viewport: Viewport): Rect => {
    const left = contentRect?.left ?? 0;
    const top = contentRect?.top ?? 0;
    return { left, top, width: Math.max(viewport.width - left, 0), height: Math.max(viewport.height - top, 0) };
};

/**
 * Where the panel sits. Expanded fills the content area (left menu and header stay visible). A corner dock holds the
 * panel by its outer bottom corner, a third of the content area wide unless the user sized it, from the header's gap
 * down to the row on the window's bottom edge when `reserveBottomRow` (bottom right only: nothing sits bottom left),
 * else to its side gap off that edge. The side dock is the window's full height at its right edge, between the
 * minimum width and 40% of the window. The width goes with the panel to every dock, the height between the corners.
 *
 * Corners are measured off the content area rather than the viewport, so the panel never reaches under the left
 * menu (which collapses) or into the header (which grows with its indicator banners).
 *
 * The history column is added to the panel's width rather than taken out of it, so opening it does not squeeze the
 * conversation, until the content area runs out of room, where the clamp takes over and the two share what is left.
 * Expanded already fills the content area, so there the column comes out of that width.
 */
export const resolvePanelFrame = ({
    placement,
    isExpanded,
    isHistoryOpen,
    reserveBottomRow,
    contentRect,
    viewport,
}: PanelFrameInput): PanelFrame => {
    const area = dockArea(contentRect, viewport);
    const canMove = area.width >= MIN_WIDTH + 2 * SIDE_GAP;
    const canSideDock = canMove && viewport.width >= SIDE_DOCK_MIN_VIEWPORT;
    const placed = canMove ? placement : DEFAULT_PLACEMENT;
    const dock = placed.side === 'side' && !canSideDock ? placed.corner : placed.side;
    const historyWidth = isHistoryOpen ? HISTORY_WIDTH : 0;
    const docked = { dock, canMove, canSideDock };

    if (isExpanded) {
        const box = { left: area.left, top: area.top, width: area.width, height: area.height };
        return { ...docked, box, style: box, width: null, height: null };
    }

    const defaultWidth = Math.max(Math.round(area.width * WIDTH_RATIO), MIN_WIDTH);

    if (dock === 'side') {
        const width = extent(
            placed.width ?? defaultWidth,
            MIN_WIDTH,
            Math.max(Math.round(viewport.width * SIDE_DOCK_MAX_SHARE), MIN_WIDTH)
        );
        const boxWidth = width.now + historyWidth;
        return {
            ...docked,
            box: { left: viewport.width - boxWidth, top: 0, width: boxWidth, height: viewport.height },
            style: { right: 0, top: 0, width: boxWidth, height: viewport.height },
            width,
            height: null,
        };
    }

    // Never negative: a content area narrower than the gaps it reserves would otherwise yield an invalid width.
    const maxWidth = Math.max(area.width - 2 * SIDE_GAP - historyWidth, 0);
    const width = extent(placed.width ?? defaultWidth, Math.min(MIN_WIDTH, maxWidth), maxWidth);
    const top = area.top + TOP_GAP;
    const bottom = viewport.height - (dock === 'right' && reserveBottomRow ? BOTTOM_GAP : SIDE_GAP);
    // The minimum outranks the bottom gap once the two edges close in, so a very short window gets a panel running
    // past its bottom gap, over whatever row sits there, instead of an unusable sliver.
    const maxHeight = Math.max(bottom - top, MIN_HEIGHT);
    const height = extent(placed.height ?? maxHeight, MIN_HEIGHT, maxHeight);
    const boxWidth = width.now + historyWidth;
    const boxTop = Math.max(bottom - height.now, top);
    const left = dock === 'right' ? viewport.width - SIDE_GAP - boxWidth : area.left + SIDE_GAP;

    return {
        ...docked,
        box: { left, top: boxTop, width: boxWidth, height: height.now },
        style:
            dock === 'right'
                ? { right: SIDE_GAP, top: boxTop, width: boxWidth, height: height.now }
                : { left, top: boxTop, width: boxWidth, height: height.now },
        width,
        height,
    };
};

/**
 * Whether the panel has room for the history column beside a readable conversation. On a narrow content area the
 * panel is capped at what fits, so the column would otherwise take its fixed width out of an already-small panel and
 * leave the conversation a sliver, or nothing at all.
 */
export const canFitHistoryColumn = (input: Omit<PanelFrameInput, 'isHistoryOpen'>): boolean =>
    resolvePanelFrame({ ...input, isHistoryOpen: true }).box.width - HISTORY_WIDTH >= MIN_CONVERSATION_WIDTH;

/**
 * The dock a dragged panel lands in: the side dock while the pointer is at the window's right edge, or for a drag
 * out of the side dock that has not left its strip; otherwise the corner nearer the panel's centre, against the
 * middle of the content area.
 */
export const landingDock = ({
    pointerX,
    panelCentreX,
    inSideStrip,
    canSideDock,
    contentRect,
    viewport,
}: {
    pointerX: number;
    panelCentreX: number;
    inSideStrip: boolean;
    canSideDock: boolean;
    contentRect: Rect | null;
    viewport: Viewport;
}): PanelDock => {
    if (canSideDock && (inSideStrip || pointerX >= viewport.width - SIDE_DOCK_EDGE)) {
        return 'side';
    }
    const area = dockArea(contentRect, viewport);
    return panelCentreX < area.left + area.width / 2 ? 'left' : 'right';
};

const DOCK_ORDER: PanelDock[] = ['left', 'right', 'side'];

/** Alt+Shift+← / → step through bottom left, bottom right and the side dock, in that order, stopping at either end. */
export const stepDock = (dock: PanelDock, step: -1 | 1, canSideDock: boolean): PanelDock => {
    const order = canSideDock ? DOCK_ORDER : DOCK_ORDER.slice(0, 2);
    return order[clamp(order.indexOf(dock) + step, 0, order.length - 1)];
};

/** A double-click on the header: the other corner, or out of the side dock back to the corner it came from. */
export const toggledDock = (dock: PanelDock, corner: PanelCorner): PanelDock =>
    dock === 'side' ? corner : dock === 'left' ? 'right' : 'left';

/** The placement once docked in `dock`: a corner becomes the one the side dock returns to. */
export const dockedIn = (placement: PanelPlacement, dock: PanelDock): PanelPlacement => ({
    ...placement,
    side: dock,
    corner: dock === 'side' ? placement.corner : dock,
});

/** The panel lifted out of the side dock at its corner size, centred on the pointer with its header under it. */
export const liftedBox = (
    size: { width: number; height: number },
    pointer: { x: number; y: number },
    grabY: number,
    viewport: Viewport
): Rect => ({
    left: clamp(pointer.x - size.width / 2, 0, viewport.width - size.width),
    top: clamp(pointer.y - grabY, 0, viewport.height - size.height),
    ...size,
});

const isDock = (value: unknown): value is PanelDock => value === 'left' || value === 'right' || value === 'side';
const size = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/** A remembered placement, or the default for anything else in its place. */
export const parsePlacement = (stored: string | null | undefined): PanelPlacement => {
    try {
        const value: unknown = JSON.parse(stored ?? '');
        if (typeof value !== 'object' || value === null) {
            return DEFAULT_PLACEMENT;
        }
        const { side, corner, width, height } = value as Record<string, unknown>;
        if (!isDock(side) || (corner !== 'left' && corner !== 'right')) {
            return DEFAULT_PLACEMENT;
        }
        return { side, corner, width: size(width), height: size(height) };
    } catch {
        return DEFAULT_PLACEMENT;
    }
};
